import { Navigate, useSearchParams, Link } from 'react-router-dom'
import { UserCog } from 'lucide-react'
import AutomaticosTab from './LembretesCobranca/AutomaticosTab'
import type { CobrancaAcaoEtapa } from '../../types'

/**
 * Régua de cobrança — vigente desde 11/08/2026.
 *
 *   D-3, D0, D+3, D+7 → WhatsApp automático (cron → lembrete_fila → UaizyChat); D-1 até o D0 entrar
 *   D+10, D+17, D+24 → contato humano, com responsável e registro
 *
 * Esta tela mostra só o lado automático. O contato humano, que antes morava na
 * aba "Manuais" daqui, foi absorvido pelo Painel de Cobrança e Documentos —
 * lá ele convive com notificação de mora, advertência, distrato e negativação,
 * que são a continuação natural da régua.
 *
 * O redirecionamento abaixo não é decoração: notificações do sininho entregues
 * antes da migração apontam para `?aba=manuais` e continuariam chegando por
 * meses. Elas precisam cair na fila certa da esteira, não numa aba que sumiu.
 */

const FILA_DA_ETAPA: Record<CobrancaAcaoEtapa, string> = {
    'D+4': 'regua_d4',
    'D+10': 'regua_d10',
    'D+17': 'regua_d17',
    'D+24': 'regua_d24',
}

export default function LembretesCobrancaView() {
    const [searchParams] = useSearchParams()

    if (searchParams.get('aba') === 'manuais') {
        const etapa = searchParams.get('etapa') as CobrancaAcaoEtapa | null
        const fila = etapa ? FILA_DA_ETAPA[etapa] : null
        // Sem etapa, o link genérico cai no grupo da régua inteiro.
        const destino = fila ? `/financeiro/esteira?fila=${encodeURIComponent(fila)}` : '/financeiro/esteira'
        return <Navigate to={destino} replace />
    }

    return (
        <div className="p-4 md:p-6 space-y-6">
            <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">Lembretes de Cobrança</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Régua de inadimplência — <strong>D-3, no dia e D+3</strong> saem sozinhos por WhatsApp; o <strong>D+7</strong> só sai se ninguém registrou contato.
                </p>
            </div>

            <Link
                to="/financeiro/esteira"
                className="flex items-start gap-3 p-4 rounded-lg border border-primary/20 bg-primary/5 hover:bg-primary/10 transition-colors"
            >
                <UserCog className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                        O contato humano mudou de lugar
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        D+10, D+17 e D+24 agora ficam no <strong>Painel de Cobrança e Documentos</strong>,
                        junto com notificação de mora, advertência, distrato e negativação.
                    </p>
                </div>
            </Link>

            <AutomaticosTab />
        </div>
    )
}
